import { apiFetch } from '@/platform/lib/api';

export interface ClaimPurchaseFields {
  id: string;
  purchasePageUrl?: string;
  purchasePageLabel?: string;
  hasPurchasePage: boolean;
  vendorLinkStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  sellerUserId?: string;
}

export function updatePurchasePage(
  claimId: string,
  body: { purchase_page_url?: string; purchase_page_label?: string },
) {
  return apiFetch<ClaimPurchaseFields>(`/claims/${claimId}/purchase-page`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export interface VendorLinkRequest {
  id: string;
  claimId: string;
  sellerUserId: string;
  purchasePageUrl: string;
  message?: string;
  status: string;
}

export function getPendingVendorLink(claimId: string) {
  return apiFetch<{ request: VendorLinkRequest | null }>(`/claims/${claimId}/vendor-link-request`);
}

export function createVendorLinkRequest(claimId: string, message?: string) {
  return apiFetch<VendorLinkRequest>(`/claims/${claimId}/vendor-link-requests`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export function approveVendorLink(claimId: string, requestId: string) {
  return apiFetch<{ status: string }>(`/claims/${claimId}/vendor-link-requests/${requestId}/approve`, {
    method: 'POST',
  });
}

export function rejectVendorLink(claimId: string, requestId: string, ownerResponse?: string) {
  return apiFetch<{ status: string }>(`/claims/${claimId}/vendor-link-requests/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ owner_response: ownerResponse }),
  });
}

export interface StorefrontPage {
  id: string;
  url: string;
  label?: string;
  domain: string;
}

export function listStorefrontPages(vendorId: string) {
  return apiFetch<{ pages: StorefrontPage[] }>(`/vendors/${vendorId}/storefront-pages`);
}

export function addStorefrontPage(vendorId: string, url: string, label?: string) {
  return apiFetch<StorefrontPage>(`/vendors/${vendorId}/storefront-pages`, {
    method: 'POST',
    body: JSON.stringify({ url, label }),
  });
}

export function listLinkedClaims(vendorId: string) {
  return apiFetch<{ claims: ClaimPurchaseFields[] }>(`/vendors/${vendorId}/linked-claims`);
}

export function listUrlMatchSuggestions(vendorId: string) {
  return apiFetch<{ claims: ClaimPurchaseFields[] }>(`/vendors/${vendorId}/url-match-suggestions`);
}

export function listVendorLinkRequests(vendorId: string) {
  return apiFetch<{ requests: VendorLinkRequest[] }>(`/vendors/${vendorId}/vendor-link-requests`);
}

export function purchaseDomain(url?: string): string {
  if (!url) return '';
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export type StoreCatalogScope = 'public' | 'admin';

export interface VendorSettings {
  storeName: string;
  tagline?: string;
  supportEmail?: string;
  shippingPolicy?: string;
  returnPolicy?: string;
  storePublic: boolean;
  storeCatalogScope?: StoreCatalogScope;
  defaultShippingProfileId?: string;
  allowedShippingCountries?: string[];
  connectOnboardingComplete?: boolean;
  platformFeeBps?: number;
}

export type ShippingProfileKind = 'flat' | 'tier' | 'zone' | 'weight_band';

export interface VendorShippingProfile {
  id: string;
  name: string;
  kind: ShippingProfileKind;
  rateCents?: number;
  zones?: string[];
  weightMinGrams?: number;
  weightMaxGrams?: number;
  stripeShippingRateId?: string;
  displayName?: string;
  active: boolean;
}

export type ProductStatus = 'draft' | 'active' | 'archived';
export type ProductKind =
  | 'paydirt'
  | 'precious_metal'
  | 'precious_gemstone'
  | 'gemstone'
  | 'mineral'
  | 'tools'
  | 'machinery';

export interface VendorProduct {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  priceCents: number;
  currency: string;
  inventoryQty?: number;
  trackInventory: boolean;
  productKind?: ProductKind;
  weightGrams?: number;
  shippingProfileId?: string;
  shippingSurchargeCents?: number;
  stripePriceId?: string;
  imageObjectPaths?: string[];
  imageUrls?: string[];
  status: ProductStatus;
  createdAt?: string;
  updatedAt?: string;
}

export function formatPrice(cents: number, currency = 'USD') {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

export type VerificationStatus = 'none' | 'pending' | 'verified' | 'rejected';

export type SellerCapabilities = {
  termsAccepted: boolean;
  marketplaceTermsAccepted: boolean;
  connectOnboardingComplete: boolean;
  vendorVerificationStatus: VerificationStatus;
  vendorEnabled: boolean;
  canListFinds: boolean;
  canManageCatalog: boolean;
};

export type VendorState = {
  vendorEnabled: boolean;
  vendorVerificationStatus: VerificationStatus;
  vendorStatus: string;
  emailVerified: boolean;
  termsAccepted: boolean;
  marketplaceTermsAccepted: boolean;
  profileComplete: boolean;
  connectOnboardingComplete: boolean;
  vendorSuspensionReason?: string;
};

export type VerificationRequest = {
  id: string;
  status: string;
  businessName: string;
  businessDescription: string;
  supportEmail: string;
  rejectionReason?: string;
  submittedAt?: string;
};

export function getVerificationStatus() {
  return apiFetch<{
    state: VendorState;
    latestRequest?: VerificationRequest;
    canApply: boolean;
    applyBlockReason?: string;
  }>('/vendors/me/verification');
}

export function getSellerState() {
  return apiFetch<SellerCapabilities>('/vendors/me/seller-state');
}

export function pauseSelling() {
  return apiFetch<{ status: string }>('/vendors/me/seller/pause', { method: 'POST' });
}

export function applyVerification(body: {
  businessName: string;
  businessDescription: string;
  supportEmail: string;
  existingShopUrls?: string[];
  acceptedMarketplaceTerms: boolean;
}) {
  return apiFetch<VerificationRequest>('/vendors/me/verification/apply', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function withdrawVerification() {
  return apiFetch<{ status: string }>('/vendors/me/verification/withdraw', { method: 'POST' });
}

export function acceptMarketplaceSellerTerms() {
  return apiFetch<{ status: string }>('/vendors/me/marketplace-terms/accept', {
    method: 'POST',
    body: JSON.stringify({ acceptedMarketplaceTerms: true }),
  });
}

export function startConnectOnboarding(vendorId: string) {
  return apiFetch<{ accountId: string; onboardingUrl: string }>(`/vendors/${vendorId}/connect/onboarding`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function getConnectStatus(vendorId: string) {
  return apiFetch<{
    stripeConnectAccountId?: string;
    connectChargesEnabled: boolean;
    connectPayoutsEnabled: boolean;
    connectOnboardingComplete: boolean;
  }>(`/vendors/${vendorId}/connect/status`);
}

export function syncVendorConnect(vendorId: string) {
  return apiFetch<{
    stripeConnectAccountId?: string;
    connectChargesEnabled: boolean;
    connectPayoutsEnabled: boolean;
    connectOnboardingComplete: boolean;
  }>(`/vendors/${vendorId}/connect/sync`, { method: 'POST', body: '{}' });
}

export function createConnectAccountSession(vendorId: string) {
  return apiFetch<{ clientSecret: string }>(`/vendors/${vendorId}/connect/account-session`, {
    method: 'POST',
    body: '{}',
  });
}

export function createExpressLoginLink(vendorId: string) {
  return apiFetch<{ url: string }>(`/vendors/${vendorId}/connect/express-login`, {
    method: 'POST',
    body: '{}',
  });
}

export function getVendorSettings(vendorId: string) {
  return apiFetch<VendorSettings>(`/vendors/${vendorId}/settings`);
}

export function updateVendorSettings(vendorId: string, body: Partial<VendorSettings>) {
  return apiFetch<VendorSettings>(`/vendors/${vendorId}/settings`, {
    method: 'PATCH',
    body: JSON.stringify({
      storeName: body.storeName,
      tagline: body.tagline,
      supportEmail: body.supportEmail,
      shippingPolicy: body.shippingPolicy,
      returnPolicy: body.returnPolicy,
      storePublic: body.storePublic,
      storeCatalogScope: body.storeCatalogScope,
      defaultShippingProfileId: body.defaultShippingProfileId,
      allowedShippingCountries: body.allowedShippingCountries,
    }),
  });
}

export function listShippingProfiles(vendorId: string) {
  return apiFetch<{ profiles: VendorShippingProfile[] }>(`/vendors/${vendorId}/shipping-profiles`);
}

export function createShippingProfile(
  vendorId: string,
  body: Omit<VendorShippingProfile, 'id' | 'stripeShippingRateId'>,
) {
  return apiFetch<VendorShippingProfile>(`/vendors/${vendorId}/shipping-profiles`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateShippingProfile(
  vendorId: string,
  profileId: string,
  body: Partial<VendorShippingProfile>,
) {
  return apiFetch<VendorShippingProfile>(`/vendors/${vendorId}/shipping-profiles/${profileId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteShippingProfile(vendorId: string, profileId: string) {
  return apiFetch<{ status: string }>(`/vendors/${vendorId}/shipping-profiles/${profileId}`, {
    method: 'DELETE',
  });
}

export function listVendorProducts(vendorId: string) {
  return apiFetch<{ products: VendorProduct[] }>(`/vendors/${vendorId}/products`);
}

export function getVendorProduct(vendorId: string, productId: string) {
  return apiFetch<VendorProduct>(`/vendors/${vendorId}/products/${productId}`);
}

export function createVendorProduct(
  vendorId: string,
  body: Omit<VendorProduct, 'id' | 'createdAt' | 'updatedAt'>,
) {
  return apiFetch<VendorProduct>(`/vendors/${vendorId}/products`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateVendorProduct(vendorId: string, productId: string, body: Partial<VendorProduct>) {
  return apiFetch<VendorProduct>(`/vendors/${vendorId}/products/${productId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteVendorProduct(vendorId: string, productId: string) {
  return apiFetch<{ status: string }>(`/vendors/${vendorId}/products/${productId}`, {
    method: 'DELETE',
  });
}

export function deleteStorefrontPage(vendorId: string, pageId: string) {
  return apiFetch<{ status: string }>(`/vendors/${vendorId}/storefront-pages/${pageId}`, {
    method: 'DELETE',
  });
}
